import {
  compact,
  every,
  filter,
  includes,
  isNumber,
  isPlainObject,
  map,
  some,
  sum,
  toLower,
  trim,
  values,
} from 'es-toolkit/compat';
import { Annotation, AnnotationSummary, AnnotationWorkingStates } from 'interfaces/annotation';

export const NOT_ASSIGNED_DISPLAY_STATUS = 'notAssigned' as const;

export type AnnotationAssignmentDisplayStatus = AnnotationWorkingStates | typeof NOT_ASSIGNED_DISPLAY_STATUS;

export const ANNOTATION_ASSIGNMENT_DISPLAY_STATUS_OPTIONS: AnnotationAssignmentDisplayStatus[] = [
  NOT_ASSIGNED_DISPLAY_STATUS,
  AnnotationWorkingStates.Annotating,
  AnnotationWorkingStates.QA,
  AnnotationWorkingStates.Done,
];

export const ANNOTATION_ASSIGNMENT_IN_PROGRESS_DISPLAY_STATUSES: AnnotationAssignmentDisplayStatus[] = [
  AnnotationWorkingStates.Annotating,
  AnnotationWorkingStates.QA,
];

export const ANNOTATION_ASSIGNMENT_DONE_ONLY_DISPLAY_STATUSES: AnnotationAssignmentDisplayStatus[] = [
  AnnotationWorkingStates.Done,
];

export const ANNOTATION_ASSIGNMENT_DISPLAY_STATUS_LABELS: Record<AnnotationAssignmentDisplayStatus, string> = {
  [NOT_ASSIGNED_DISPLAY_STATUS]: 'Not assigned',
  [AnnotationWorkingStates.Annotating]: 'Annotating',
  [AnnotationWorkingStates.QA]: 'QA',
  [AnnotationWorkingStates.Done]: 'Done',
};

export interface AnnotationAssignmentTableFilters {
  searchText: string;
  mineOnly: boolean;
  selectedTaggerIds: number[];
  selectedStatuses: AnnotationAssignmentDisplayStatus[];
  currentUserId?: string;
}

const WORKING_STATE_SORT_ORDER: Record<AnnotationAssignmentDisplayStatus, number> = {
  [NOT_ASSIGNED_DISPLAY_STATUS]: 0,
  [AnnotationWorkingStates.Annotating]: 1,
  [AnnotationWorkingStates.QA]: 2,
  [AnnotationWorkingStates.Done]: 3,
};

export const getTotalAnnotationsCount = (annotationSummary?: AnnotationSummary): number => {
  return sum(
    map(values(annotationSummary || {}), (value) => {
      if (isNumber(value)) {
        return value;
      }

      if (value && isPlainObject(value)) {
        return (value.positive || 0) + (value.negative || 0);
      }

      return 0;
    })
  );
};

export const getAnnotationDisplayStatus = (annotation: Annotation): AnnotationAssignmentDisplayStatus => {
  const hasAssignee = annotation.taggerId != null || annotation.tagger != null;
  const isEmptyAnnotation =
    getTotalAnnotationsCount(annotation.annotationSummary) === 0 &&
    getTotalAnnotationsCount(annotation.draftAnnotationSummary) === 0;

  if (!hasAssignee && isEmptyAnnotation) {
    return NOT_ASSIGNED_DISPLAY_STATUS;
  }

  return annotation.workingState;
};

export const getAnnotationWorkingStateSortValue = (workingState?: AnnotationAssignmentDisplayStatus | string) => {
  if (!workingState) return Number.MAX_SAFE_INTEGER;
  return WORKING_STATE_SORT_ORDER[workingState as AnnotationAssignmentDisplayStatus] ?? Number.MAX_SAFE_INTEGER;
};

export const matchesDisplayStatusShortcut = ({
  selectedStatuses,
  shortcutStatuses,
}: {
  selectedStatuses: AnnotationAssignmentDisplayStatus[];
  shortcutStatuses: AnnotationAssignmentDisplayStatus[];
}) => {
  return (
    selectedStatuses.length === shortcutStatuses.length &&
    every(shortcutStatuses, (status) => includes(selectedStatuses, status))
  );
};

export const filterAnnotationRows = ({
  rows,
  searchText,
  mineOnly,
  selectedTaggerIds,
  selectedStatuses,
  currentUserId,
}: AnnotationAssignmentTableFilters & { rows?: Annotation[] }) => {
  const normalizedSearchText = toLower(trim(searchText));

  return filter(rows ?? [], (annotation) => {
    const displayStatus = getAnnotationDisplayStatus(annotation);

    if (mineOnly && currentUserId && annotation.tagger?.authUserId !== currentUserId) {
      return false;
    }

    if (
      selectedTaggerIds.length > 0 &&
      !includes(selectedTaggerIds, annotation.tagger?.taggerId ?? annotation.taggerId)
    ) {
      return false;
    }

    if (selectedStatuses.length > 0 && !includes(selectedStatuses, displayStatus)) {
      return false;
    }

    if (!normalizedSearchText) {
      return true;
    }

    const searchableFields = compact([
      annotation.slideId,
      annotation.tagger?.name,
      ANNOTATION_ASSIGNMENT_DISPLAY_STATUS_LABELS[displayStatus],
    ]);

    return some(searchableFields, (value) => toLower(value).includes(normalizedSearchText));
  });
};
